import { humanFriendlyTicks } from '@design/charts/helpers/human-friendly-ticks/human-friendly-ticks';
import * as d3 from 'd3';
import { useEffect, useRef } from 'react';

import styles from './HorizontalGridLines.module.scss';

interface HorizontalGridLinesProps {
  yScale: d3.AxisScale<number>;
  tickSize: number;
  tickCount?: number;
  tickValues?: number[];
  format?: (n: number, fractionDigit?: number) => string;
  tickVariant?: 'domain' | 'nice';
  isBordered?: boolean;
  yAxisLabel?: string;
  rightAxisWidth: number;
}

export const HorizontalGridLines = ({
  yScale,
  tickSize,
  tickCount = 5,
  tickValues,
  format = d3.format('.0%'),
  tickVariant = 'domain',
  isBordered = false,
  yAxisLabel,
  rightAxisWidth,
}: HorizontalGridLinesProps) => {
  const gRef = useRef<SVGGElement>(null);
  const [start, stop] = yScale.domain();

  useEffect(() => {
    if (!gRef.current) {
      return;
    }

    const axisGroup = d3.select(gRef.current);

    let ticks: number[];
    let step: number;

    if (tickValues?.length) {
      ticks = tickValues;
      step = tickValues.length > 1 ? tickValues[1] - tickValues[0] : 1;
    } else {
      const tickScale =
        tickVariant === 'domain'
          ? [start, stop]
          : d3.nice(start, stop, tickCount);

      ({ ticks, step } = humanFriendlyTicks(tickScale, tickCount));
    }

    if (isBordered) {
      ticks = ticks.concat([start, stop]);
    }

    // decimals needed to distinguish ticks one step apart, e.g. step 0.05 -> 2
    const fractionDigits = Math.max(0, -Math.floor(Math.log10(step)));

    const preciseFormat = (val: number) => format(val, fractionDigits);
    axisGroup.call(
      d3
        .axisRight(yScale)
        .tickValues(ticks)
        .tickSize(-tickSize)
        .tickFormat(preciseFormat)
        .tickPadding(10)
    );
    axisGroup.selectAll('.domain').remove();
  }, [tickSize, yScale, tickValues]);

  return (
    <g>
      <g className={styles.gridLinesGroup} ref={gRef} />
      <g transform={`translate(${rightAxisWidth}, ${yScale.range()[0] / 2})`}>
        <text className={styles.yAxisLabel} dy={'-1.5em'}>
          {yAxisLabel}
        </text>
      </g>
    </g>
  );
};
